import React, { useState } from "react";
import ReactModal from "react-modal";
import Select from "react-select";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";

import ai from "../../../helpers/axios";
import TextAreaInput from "../../textarea-input";
import { LoadingButton } from "../../shared/loading-button";

const SCORE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${n}` }));

const CRITERIA = [
  { key: "flightOverall", label: "Flight Overall" },
  { key: "communication", label: "Communication" },
  { key: "brokerFriendly", label: "Broker-friendly" },
  { key: "clientFeedback", label: "Client Feedback" },
];

const toOption = (value) =>
  value || value === 0
    ? SCORE_OPTIONS.find((o) => o.value === value) || null
    : null;

const OperatorRatingModal = ({
  isOpen,
  onClose,
  tripId,
  operator,
  existingRating,
  onSaved,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [saving, setSaving] = useState(false);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");

  const [initKey, setInitKey] = useState(null);
  const key = `${operator?.id}-${isOpen}`;
  if (isOpen && key !== initKey) {
    setScores({
      flightOverall: existingRating?.flightOverall ?? null,
      communication: existingRating?.communication ?? null,
      brokerFriendly: existingRating?.brokerFriendly ?? null,
      clientFeedback: existingRating?.clientFeedback ?? null,
    });
    setNotes(existingRating?.notes || "");
    setInitKey(key);
  }

  const handleSave = async () => {
    const missing = CRITERIA.some(
      ({ key: ck }) => scores[ck] === null || scores[ck] === undefined,
    );
    if (missing) {
      toast.error("Please complete all required rating fields.");
      return;
    }

    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      await ai.auth(token).put(`/api/trips/${tripId}/operator-ratings`, {
        operatorId: operator?.id,
        operatorName: operator?.name,
        flightOverall: scores.flightOverall,
        communication: scores.communication,
        brokerFriendly: scores.brokerFriendly,
        clientFeedback: scores.clientFeedback,
        notes,
      });
      toast.success("Operator rating saved");
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving operator rating:", error);
      toast.error("Failed to save operator rating");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={onClose}
      style={{
        overlay: {
          zIndex: 999999,
          backgroundColor: "rgb(32 53 68 / 50%)",
        },
        content: {
          width: "auto",
          border: "none",
          maxWidth: "600px",
          maxHeight: "80vh",
          minWidth: "calc(min(500px, 95vw))",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header">
        <h4>OPI Review</h4>
      </div>

      <div className="modal-body width-100">
        <form className="w-100 base-form mt-10">
          <h6>Ratings</h6>
          <p className="mb-1">
            Please rate the Operator in the following categories.
          </p>
          <p className="mb-20">1 is the worst, 5 is the best</p>

          <div className="w-100 d-flex gap-20 flex-wrap">
            {CRITERIA.map(({ key: ck, label }) => (
              <div className="form-group tv-modal-field-half" key={ck}>
                <label>{label} <span className="required">*</span></label>

                <Select
                  className="base-select"
                  classNamePrefix="select"
                  name={ck}
                  options={SCORE_OPTIONS}
                  value={toOption(scores[ck])}
                  onChange={(opt) =>
                    setScores((s) => ({ ...s, [ck]: opt?.value ?? null }))
                  }
                  placeholder="1-5"
                />
              </div>
            ))}
          </div>

          <h6 className="mt-20">Notes</h6>
          <p className="mb-1">
            Please enter any notes about the Operator that may be relevant for
            future bookings.
          </p>
          <TextAreaInput
            hideLabel
            name="operator-rating-notes"
            value={notes}
            handleChange={(e) => setNotes(e.target.value)}
            placeholder="Enter notes here"
            rows={3}
          />
        </form>
      </div>

      <div className="modal-footer w-100 mt-10 d-flex gap-10">
        <LoadingButton
          className="base-button evo-blue flex-half"
          isLoading={saving}
          onClick={handleSave}
        >
          Submit
        </LoadingButton>
        <button
          type="button"
          className="base-button btn-danger flex-half"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </ReactModal>
  );
};

export default OperatorRatingModal;
